import { Download, ExternalLink, File, Folder, Loader2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { FileAttachment } from "../../models/Message";
import { Util } from "../../services/Utils/Util";
import { downloadFile } from "../../services/Utils/UtilDownload";
import ImageViewer from "../components/ImageViewer";
import { UIRoomItem } from "../../models/RoomItem";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { DocumentViewerModal, isViewerSupported, isInlineViewable, isBrowserTabViewable } from "./DocumentViewerModal";

const MessageFileItem: React.FC<{ file: FileAttachment, files: FileAttachment[], showTitle: boolean }> = ({ file, files, showTitle }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const fileUrls = files.map(f => f.url);

  const isVideoFile = (filename: string) => {
    const ext = filename?.split('.').pop()?.toLowerCase() ?? "";
    return ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'].includes(ext);
  };

  const openImageViewer = useCallback((index: any) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  if (file.group == 'image') {
    return (
      <div className="relative inline-block overflow-hidden rounded-lg border border-border bg-background" onClick={(e) => e.stopPropagation()}>

        {!file.url && (
          <div className="flex h-20 w-20 items-center justify-center bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {file.url && (
          <img
            src={file.url}
            onClick={(e) => {
              e.stopPropagation();
              openImageViewer(fileUrls.findIndex(url => url == file.url));
            }}
            alt={file.filename}
            className="max-w-[200px] max-h-[200px] cursor-pointer object-contain hover:opacity-90 rounded"
            title={file.filename}
          />
        )}
        {showTitle && <p className="px-2 py-1 text-xs text-[color:rgb(var(--text-body))] truncate max-w-[200px]">{file.filename}</p>}
        {isViewerOpen && <ImageViewerModal images={fileUrls} currentImage={currentImage} onClose={closeImageViewer} />}
      </div>
    );
  } else if (isVideoFile(file.filename)) {
    // 동영상 파일은 앱 내에서 재생
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <div
          className="flex items-center gap-3 p-2 border border-border rounded-lg bg-background max-w-[280px] overflow-hidden cursor-pointer hover:bg-muted transition-colors"
          onClick={() => setIsVideoOpen(true)}
        >
          <FileIcon filename={file.filename} />
          {!file.url && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium text-[color:rgb(var(--text-body))] truncate">{file.filename}</p>
            {file.sizeByte > 0 && <p className="text-xs text-muted-foreground">{Util.niceBytes(file.sizeByte)}</p>}
          </div>

        </div>
        {isVideoOpen && <VideoViewerModal videoUrl={file.url} onClose={() => setIsVideoOpen(false)} />}
      </div>
    );
  } else {
    // 파일 열기 우선순위: 뷰어 > 브라우저 > 다운로드
    //   1) 뷰어 모달
    //      - CrossViewer 지원(오피스/PDF/한글) → DocumentViewerModal
    //      - 텍스트/데이터(txt·csv·json 등) → InlineViewerModal (iframe, 앱 내부)
    //   2) 브라우저 새 탭(웹 전용): 뷰어로는 안 되지만 브라우저가 렌더/재생(오디오·html 등)
    //   3) 그 외(zip 등) 또는 네이티브에서의 2번 → 다운로드
    const viewerSupported = isViewerSupported(file.filename);
    const inlineViewable = !viewerSupported && isInlineViewable(file.filename);
    const openInBrowser = !viewerSupported && !inlineViewable
      && isBrowserTabViewable(file.filename) && !Capacitor.isNativePlatform();
    const [docViewerOpen, setDocViewerOpen] = useState(false);
    const [inlineViewerOpen, setInlineViewerOpen] = useState(false);

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <div
          onClick={async () => {
            if (!file.url) return;
            if (viewerSupported) {
              setDocViewerOpen(true);
            } else if (inlineViewable) {
              setInlineViewerOpen(true);
            } else if (openInBrowser) {
              window.open(file.url, '_blank', 'noopener,noreferrer');
            } else if (!isDownloading) {
              setIsDownloading(true);
              try {
                await downloadFile(file.url, file.filename);
              } finally {
                setIsDownloading(false);
              }
            }
          }}
          className="block text-inherit no-underline hover:bg-muted rounded-lg transition-colors max-w-full cursor-pointer relative"
        >
          <div className="flex items-center gap-3 p-2 border border-border rounded-lg bg-background max-w-[280px] overflow-hidden">
            <FileIcon filename={file.filename} />
            {!file.url && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium text-[color:rgb(var(--text-body))] truncate">{file.filename}</p>
              {file.sizeByte > 0 && <p className="text-xs text-muted-foreground">{Util.niceBytes(file.sizeByte)}</p>}
            </div>
          </div>
          {isDownloading && (
            <div className="absolute inset-0 bg-background/70 flex items-center justify-center rounded-lg">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          )}
        </div>
        {docViewerOpen && file.url && (
          <DocumentViewerModal
            url={file.url}
            title={file.filename}
            onClose={() => setDocViewerOpen(false)}
          />
        )}
        {inlineViewerOpen && file.url && (
          <InlineViewerModal
            url={file.url}
            filename={file.filename}
            onClose={() => setInlineViewerOpen(false)}
          />
        )}
      </div>
    )
  }
}

// FileIcon

const getFileColorStyles = (extension: string) => {
  const ext = extension.toLowerCase();

  const colors: Record<string, { bg: string, text: string, icon: string }> = {
    // Documents
    pdf: { bg: "bg-destructive/15", text: "text-destructive", icon: "text-destructive" },
    doc: { bg: "bg-blue-100", text: "text-blue-700", icon: "text-blue-500" },
    docx: { bg: "bg-blue-100", text: "text-blue-700", icon: "text-blue-500" },
    txt: { bg: "bg-muted", text: "text-[color:rgb(var(--text-body))]", icon: "text-muted-foreground" },

    // Spreadsheets
    xls: { bg: "bg-green-500/15", text: "text-green-600", icon: "text-green-500" },
    xlsx: { bg: "bg-green-500/15", text: "text-green-600", icon: "text-green-500" },
    csv: { bg: "bg-green-500/15", text: "text-green-600", icon: "text-green-500" },

    // Presentations
    ppt: { bg: "bg-primary/15", text: "text-primary", icon: "text-primary" },
    pptx: { bg: "bg-primary/15", text: "text-primary", icon: "text-primary" },

    // Archives
    zip: { bg: "bg-yellow-500/15", text: "text-yellow-600", icon: "text-yellow-600" },
    rar: { bg: "bg-yellow-500/15", text: "text-yellow-600", icon: "text-yellow-600" },
    "7z": { bg: "bg-yellow-500/15", text: "text-yellow-600", icon: "text-yellow-600" },
    tar: { bg: "bg-yellow-500/15", text: "text-yellow-600", icon: "text-yellow-600" },
    gz: { bg: "bg-yellow-500/15", text: "text-yellow-600", icon: "text-yellow-600" },

    // Code
    js: { bg: "bg-yellow-500/10", text: "text-yellow-600", icon: "text-yellow-500" },
    ts: { bg: "bg-blue-500/10", text: "text-blue-600", icon: "text-blue-500" },
    jsx: { bg: "bg-blue-500/10", text: "text-blue-600", icon: "text-blue-500" },
    tsx: { bg: "bg-blue-500/10", text: "text-blue-600", icon: "text-blue-500" },
    html: { bg: "bg-primary/10", text: "text-primary", icon: "text-primary" },
    css: { bg: "bg-blue-500/10", text: "text-blue-600", icon: "text-blue-500" },
    json: { bg: "bg-muted", text: "text-muted-foreground", icon: "text-muted-foreground" },

    // Media
    mp3: { bg: "bg-purple-100", text: "text-purple-700", icon: "text-purple-500" },
    wav: { bg: "bg-purple-100", text: "text-purple-700", icon: "text-purple-500" },
    mp4: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },
    avi: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },
    mov: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },

    // Images (fallback if not rendered as preview)
    jpg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    jpeg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    png: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    gif: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    svg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
  };

  return colors[ext] || { bg: "bg-muted", text: "text-[color:rgb(var(--text-body))]", icon: "text-muted-foreground" };
};

export const FileIcon: React.FC<{ filename: string, className?: string }> = ({ filename, className }) => {
  const extension = filename?.split('.').pop()?.toLowerCase() ?? "";
  const styles = getFileColorStyles(extension);
  const displayExt = extension.length > 4 ? extension.slice(0, 3) : extension;

  return (
    <div className={cn("relative flex flex-col items-center justify-center rounded-lg overflow-hidden shrink-0", styles.bg, className || "h-10 w-10")}>
      <File className={cn("h-5 w-5 mb-2", styles.icon)} strokeWidth={2} />
      <span className={cn("absolute bottom-1 text-meta-sm font-bold uppercase leading-none tracking-tighter", styles.text)}>
        {displayExt}
      </span>
    </div>
  );
};

export const FileIconList: React.FC<{ uitem: UIRoomItem }> = ({ uitem }) => {
  if (uitem.item.type === 'folder') {
    if (uitem.itemType === 'MessageFolder') {
      return (
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-50 text-blue-500">
          <Folder className="h-6 w-6 fill-current" />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-yellow-50 text-yellow-500">
        <Folder className="h-6 w-6 fill-current" />
      </div>
    );
  } else {
    const filename = uitem.item.file ? uitem.item.file.filename : uitem.item.url?.split('?')[0] ?? '';

    if (Util.isImageUrl(uitem.item.url)) {
      return <img src={uitem.item.url} className="h-10 w-10 object-cover rounded-lg border border-border" alt="file" />
    }

    return <FileIcon filename={filename} className="h-10 w-10" />;
  }
}

export const FileIconThumb: React.FC<{ filename: string, url: string }> = ({ filename, url }) => {
  return <>
    {Util.isImageUrl(url) ?
      <span><img src={url} className="h-10 w-10 object-cover rounded" alt="thumb" /></span>
      :
      <span><FileIcon filename={filename} /></span>
    }
  </>
};


export const ImageViewerModal: React.FC<{ images: string[], currentImage: number, onClose: () => void }> = ({ images, currentImage, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent hideTitle hideClose className="max-w-none w-screen h-screen p-0 bg-viewer border-none !rounded-none">
        <div className="relative w-full h-full flex items-center justify-center">
          <ImageViewer
            src={images}
            currentIndex={currentImage}
            onClose={onClose}
            backgroundStyle={{
              backgroundColor: "transparent"
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const VideoViewerModal: React.FC<{ videoUrl: string, onClose: () => void }> = ({ videoUrl, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent hideTitle hideClose className="max-w-none w-screen h-screen p-0 bg-viewer-heavy border-none !rounded-none">
        <div className="relative w-full h-full flex items-center justify-center">
          {/* 닫기 버튼 */}
          <div className="absolute top-5 right-5 z-[51] flex gap-2">
            <a href={videoUrl} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="icon" className="bg-overlay-control hover:bg-overlay-control-hover text-overlay-control-text rounded-full h-10 w-10">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
              </Button>
            </a>
            <Button
              variant="ghost"
              size="icon"
              className="bg-overlay-control hover:bg-overlay-control-hover text-overlay-control-text rounded-full h-10 w-10"
              onClick={onClose}
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* 비디오 플레이어 */}
          <video
            controls
            autoPlay
            className="max-h-[90vh] max-w-[90vw] rounded-lg"
            src={videoUrl}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * 인앱 텍스트/데이터 뷰어 — txt·csv·json·xml·md 등을 iframe 안에서
 * 브라우저 기본 렌더러로 표시(앱을 벗어나지 않음). CrossViewer 미지원 포맷용.
 */
export const InlineViewerModal: React.FC<{ url: string, filename: string, onClose: () => void }> = ({ url, filename, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent hideTitle hideClose className="max-w-none w-screen h-screen p-0 bg-background border-none !rounded-none flex flex-col">
        {/* 헤더: 파일명 + 새 탭 / 다운로드 / 닫기 */}
        <div className="flex items-center gap-1 px-4 h-12 shrink-0 border-b border-border">
          <p className="flex-1 min-w-0 truncate text-sm font-medium text-[color:rgb(var(--text-body))]" title={filename}>
            {filename}
          </p>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground">
              <ExternalLink className="w-5 h-5" />
            </Button>
          </a>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            onClick={() => downloadFile(url, filename)}
          >
            <Download className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        {/* 본문: 브라우저 기본 렌더 (흰 배경/검정 텍스트 — 파일 원문 그대로) */}
        <iframe src={url} title={filename} className="flex-1 w-full border-0 bg-white" />
      </DialogContent>
    </Dialog>
  );
};

export default MessageFileItem;